import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { client } from '@/services/api/axios.ts';
import { TNotification } from '@/features/notifications/schemas/notifications.schemas.ts';
import { NotificationResponse } from '@/features/notifications/hooks/queries/get-all-notifications.ts';
import { NotificationFilter } from '@/services/store/use-notifications-store.ts';

export const useUpdateNotificationQuery = ({
  notificationFilter,
}: {
  notificationFilter: NotificationFilter;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notification: TNotification) => {
      const { notificationId, status: notificationStatus } = notification;

      if (!notification.notificationId) return;
      const { data } = await client.patch(`/notifications/${notificationId}`, {
        status: notificationStatus,
      });
      return data;
    },

    onMutate: async (notification: TNotification) => {
      await queryClient.cancelQueries({ queryKey: ['notifications', notificationFilter] });

      const previousData = queryClient.getQueryData<
        InfiniteData<NotificationResponse> | undefined
      >(['notifications', notificationFilter]);

      queryClient.setQueryData<InfiniteData<NotificationResponse | undefined>>(
        ['notifications', notificationFilter],
        (previousInfiniteQueryData: any) => {
          return {
            ...previousInfiniteQueryData,
            pages: previousData?.pages?.map((page) => {
              const newBody = page.body.map((post) => {
                if (post.notificationId !== notification.notificationId) return post;

                return {
                  ...post,
                  status: notification.status,
                };
              });

              return {
                ...page,
                body: newBody,
                length: newBody.length,
              };
            }),
          };
        }
      );

      return { previousData };
    },

    onError: (_, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['notifications', notificationFilter], context.previousData);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};
